import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@viilutalous/common';
import { useAuth } from '../modules/auth';
import { useTenant } from './TenantContext';

const HOST_STORAGE_KEY = 'me_host_id';

const MeHostContext = createContext({
  hostId: '',
  setHostId: () => {},
  hosts: [],
  loading: false,
});

export function useMeHost() {
  return useContext(MeHostContext);
}

function hostIdOf(tnt) {
  return tnt?.host_id || tnt?.hostId || tnt?.host?.id || '';
}

function hostNameOf(tnt) {
  return tnt?.host_name || tnt?.hostName || tnt?.host?.name || hostIdOf(tnt) || '';
}

function hostsFromTenants(list) {
  const map = new Map();
  for (const tnt of list || []) {
    const id = hostIdOf(tnt);
    if (!id) continue;
    if (!map.has(id)) {
      map.set(id, { id, name: hostNameOf(tnt) || id });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Host-company scope for My Desktop (/me/*) cross-tenant views.
 * Prefers TenantContext tenants; refreshes /user/tenants once auth is ready
 * so host_id/host_name are present even if the in-memory list was incomplete.
 */
export function MeHostProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { tenants, currentTenant, loading: tenantsLoading } = useTenant();
  const [fetchedTenants, setFetchedTenants] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setFetchedTenants(null);
      setFetchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFetchLoading(true);
    api.get('/user/tenants')
      .then((res) => {
        if (!cancelled) setFetchedTenants(res.data?.tenants || []);
      })
      .catch((err) => {
        console.error('Failed to load hosts for My Desktop', err);
        if (!cancelled) setFetchedTenants([]);
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const hosts = useMemo(() => {
    const fromFetch = hostsFromTenants(fetchedTenants);
    if (fromFetch.length > 0) return fromFetch;
    const fromContext = hostsFromTenants(tenants);
    if (fromContext.length > 0) return fromContext;
    const id = hostIdOf(currentTenant);
    if (id) return [{ id, name: hostNameOf(currentTenant) || id }];
    return [];
  }, [fetchedTenants, tenants, currentTenant]);

  const loading = (tenantsLoading || fetchLoading) && hosts.length === 0;

  const [hostId, setHostId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(HOST_STORAGE_KEY) || '';
  });

  useEffect(() => {
    // Wait until host resolution finishes — clearing during the initial empty
    // state would wipe a still-valid stored hostId from a prior session.
    if (loading) return;
    if (hosts.length === 0) {
      if (hostId) setHostId('');
      return;
    }
    const valid = hostId && hosts.some((h) => h.id === hostId);
    if (!valid) {
      const preferredId = hostIdOf(currentTenant);
      const preferred = preferredId && hosts.find((h) => h.id === preferredId);
      setHostId((preferred || hosts[0]).id);
    }
  }, [hosts, hostId, currentTenant, loading]);

  useEffect(() => {
    if (hostId) {
      localStorage.setItem(HOST_STORAGE_KEY, hostId);
    } else {
      localStorage.removeItem(HOST_STORAGE_KEY);
    }
  }, [hostId]);

  const value = useMemo(
    () => ({ hostId, setHostId, hosts, loading }),
    [hostId, hosts, loading],
  );

  return (
    <MeHostContext.Provider value={value}>
      {children}
    </MeHostContext.Provider>
  );
}
